// Default configuration for MDM Reports filters
export interface FilterConfig {
  id: string;
  label: string;
  type: "select" | "autocomplete" | "multiselect";
  options?: any[];
  placeholder?: string;
  required?: boolean;
  dependency?: string; 
}

export interface DateRangeOption {
  id: string;
  label: string;
  fromDate: string;
  toDate: string;
}

export interface MdmReportsConfig {
  dateRangeOptions: DateRangeOption[];
  geographicalFilters: FilterConfig[];
  distributorFilter: FilterConfig;
  salesHierarchyFilters: FilterConfig[];
  filterConfiguration: ReportColumnConfig[];
}

// Report Card Interfaces
export interface ReportColumnConfig {
  columnName: string;
  id: string;
  label: string;
  mandatory: boolean;
  showInTable: boolean;
  tableField: string;
}

export interface ReportTableConfiguration {
  columns: ReportColumnConfig[];
}

// Sales Hierarchy Filter Configuration
export interface SalesHierarchyFilter {
  enabled: boolean;
  levelFilterLabel: string;
  levelFilterField: string; 
  valueFilterLabel: string;
  valueFilterField: string; 
  hierarchyOrder?: string[]; 
  hierarchyMap?: Record<string, string[]>; // Explicit parent -> child mapping
  rootLevels?: string[]; 
}

// Geographical Hierarchy Filter Configuration
export interface GeographicalHierarchyFilter {
  enabled: boolean;
  levelFilterLabel: string;
  levelFilterField: string; 
  valueFilterLabel: string;
  valueFilterField: string; 
  hierarchyOrder?: string[]; 
  hierarchyMap?: Record<string, string[]>;
  rootLevels?: string[];
}

// Distributor Filter Configuration
export interface DistributorFilter {
  enabled: boolean;
  label: string;
  field: string; // API field name
}

// Merged Filter Source Configuration (hardcoded options)
export interface MergedFilterSource {
  alias: string;  // Source filter alias (e.g., "field1")
  value: string;  // Option value (e.g., "612")
}

export type DateRangeAllowedUnit = 'day' | 'days' | 'week' | 'weeks' | 'month' | 'months' | 'year' | 'years';

export type DateRangeAllowed = `${number} ${DateRangeAllowedUnit}`;

export interface newReportConfig {
  id: string;
  name: string;
  getAPI: string;
  reportName: string;
  description: string;
  templateUrl: string;
  filterConfiguration?: ReportTableConfiguration;
  salesHierarchyFilter?: SalesHierarchyFilter;
  geographicalHierarchyFilter?: GeographicalHierarchyFilter;
  distributorFilter?: DistributorFilter;
  mandatory?: boolean;
  dateRangeFilter?: boolean;
  periodFilter?: boolean;
  isDistributorView: boolean;
  showDistributorType?: boolean;
  showDistributorDivision?: boolean;
  shouldShowCustomFilters?: boolean;
  columnsToHide?: string[];
  filtersToHide?: string[];
  isLiveReport?: boolean;
  mergedFilters?: Record<string, MergedFilterSource[]>; 
  showLast7DaysFilter?: boolean; 
  showLast3MonthsFilter?: boolean; 
  shouldShowCustomDateFilter?: boolean;
  isPDFReport?: boolean;
  dateRangeAllowed?: string; // Fixed date range like "1 week", "4 days", "5 months", "1 year"
}

export type ExcelColumns = {
  [cardId: string]: string[];
};

export interface ReportsSetupProps {
  setExcelMasterColumns?: (columns: ExcelColumns) => void;
}

// Helper function to get date ranges
const getDateRanges = () => {
  const today = new Date();
  
  // Live: Last 7 days from today
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  
  // Past 3 months: From 3 months ago to today
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(today.getMonth() - 3);
  
  // Format dates as DD-MM-YYYY
  const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  
  return {
    liveFrom: formatDate(sevenDaysAgo),
    liveTo: formatDate(today),
    past3MonthsFrom: formatDate(threeMonthsAgo),
    past3MonthsTo: formatDate(today),
  };
};

// Default fallback configuration
export const defaultMdmReportsConfig: MdmReportsConfig = (() => {
  const dates = getDateRanges();
  return {
    dateRangeOptions: [
      {
        id: "live",
        label: "Last",
        fromDate: dates.liveFrom,
        toDate: dates.liveTo
      },
      {
        id: "past3months",
        label: "Past 3 Months",
        fromDate: dates.past3MonthsFrom,
        toDate: dates.past3MonthsTo
      },
      {
        id: "custom",
        label: "Custom Range",
        fromDate: dates.past3MonthsFrom,
        toDate: dates.past3MonthsTo
      }
    ],
  geographicalFilters: [
    {
      id: "region",
      label: "Select region",
      type: "select",
      options: [],
      placeholder: "Select region",
      required: false
    },
    {
      id: "state",
      label: "Select state",
      type: "select",
      options: [],
      placeholder: "Select state",
      required: false,
      dependency: "region"
    },
    {
      id: "city",
      label: "Select city",
      type: "select",
      options: [],
      placeholder: "Select city",
      required: false,
      dependency: "state"
    }
  ],
  distributorFilter: {
    id: "distributor",
    label: "Select distributor",
    type: "select",
    options: [],
    placeholder: "Select distributor",
    required: false
  },
  salesHierarchyFilters: [
    {
      id: "role",
      label: "Select role",
      type: "select",
      options: [],
      placeholder: "Select role",
      required: false
    },
    {
      id: "name",
      label: "Select name",
      type: "select",
      options: [],
      placeholder: "Select name",
      required: false,
      dependency: "role"
    }
  ],
  filterConfiguration: []
};
})();

// Helper function to transform API response to filter options
export const transformApiResponseToFilterOptions = (
  apiResponse: any,
  valueKey: string = "value",
  labelKey: string = "label"
): any[] => {
  if (!apiResponse || !Array.isArray(apiResponse)) {
    return [];
  }
  
  return apiResponse.map((item: any) => ({
    value: item[valueKey],
    label: item[labelKey]
  }));
};


export function parseDateRangeAllowed(input: string): { amount: number; unit: 'day' | 'week' | 'month' | 'year' } | null {
  const match = /^(\d+)\s+(day|days|week|weeks|month|months|year|years)$/i.exec(input.trim());
  if (!match) return null;

  const amount = Number(match[1]);
  const rawUnit = match[2].toLowerCase() as DateRangeAllowedUnit;

  let unit: 'day' | 'week' | 'month' | 'year';
  if (rawUnit.startsWith('day')) unit = 'day';
  else if (rawUnit.startsWith('week')) unit = 'week';
  else if (rawUnit.startsWith('month')) unit = 'month';
  else unit = 'year';

  return { amount, unit };
}

export function getDateRangeFromAllowed(
  allowed: string,
  now: Date = new Date()
): { start: Date; end: Date } | null {
  const parsed = parseDateRangeAllowed(allowed);
  if (!parsed) return null;

  const { amount, unit } = parsed;
  const end = new Date(now);
  const start = new Date(now);

  switch (unit) {
    case 'day':
      start.setDate(start.getDate() - (amount - 1)); // inclusive "last N days"
      break;
    case 'week':
      start.setDate(start.getDate() - (amount * 7 - 1));
      break;
    case 'month':
      start.setMonth(start.getMonth() - amount);
      break;
    case 'year':
      start.setFullYear(start.getFullYear() - amount);
      break;
  }

  return { start, end };
}

export function getLabelFromAllowed(allowed: string): string {
  return `Last ${allowed}`;
}

// Initial Report Cards Configuration
export const initialReportCards: newReportConfig[] = [
  {
      "id": "OutletMasterReport",
      "name": "Outlet Master Report",
      "getAPI": "/table-generic/search?",
      "reportName": "master_report_outlet",
      "description": "This report contains secondary order data with distributor and outlet information.",
      "templateUrl": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoeuat/images/OutletPOC4-ehOerjjxnywv.xlsx",
      "mandatory": true,
      "isDistributorView": true,
      "filterConfiguration": {
          "columns": [
              { 
                  "columnName": "companycode",
                  "id": "companycode",
                  "label": "companycode",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "distroutletcode",
                  "id": "distroutletcode",
                  "label": "distroutletcode",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "outletcode",
                  "id": "outletcode",
                  "label": "outletcode",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": "outletcode"
              },
              {
                  "columnName": "outletname",
                  "id": "outlet_name",
                  "label": "outletname",
                  "mandatory": false,
                  "showInTable": true,
                  "tableField": "outlet_name"
              },
              {
                  "columnName": "contactno",
                  "id": "contactno",
                  "label": "contactno",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "beat",
                  "id": "beat",
                  "label": "beat",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "beatName",
                  "id": "beatName",
                  "label": "beatName",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "active",
                  "id": "active",
                  "label": "active",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "dateofopening",
                  "id": "dateofopening",
                  "label": "dateofopening",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "dateofclosing",
                  "id": "dateofclosing",
                  "label": "dateofclosing",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet email ID",
                  "id": "Outlet email ID",
                  "label": "Outlet email ID",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet's Channel Code",
                  "id": "Outlet's Channel Code",
                  "label": "Outlet's Channel Code",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "channel",
                  "id": "channel",
                  "label": "channel",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "subchannel",
                  "id": "subchannel",
                  "label": "subchannel",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "outletcategory",
                  "id": "outletcategory",
                  "label": "outletcategory",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "outletClass",
                  "id": "outletClass",
                  "label": "outletClass",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "outlettype",
                  "id": "outlettype",
                  "label": "outlettype",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "outletdivision",
                  "id": "outletdivision",
                  "label": "outletdivision",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "distributionchannel",
                  "id": "distributionchannel",
                  "label": "distributionchannel",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "marketid",
                  "id": "marketid",
                  "label": "marketid",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "marketname",
                  "id": "marketname",
                  "label": "marketname",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "VPO",
                  "id": "VPO",
                  "label": "VPO",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "account",
                  "id": "account",
                  "label": "account",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "segment",
                  "id": "segment",
                  "label": "segment",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "address 1",
                  "id": "address 1",
                  "label": "address 1",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "address 2",
                  "id": "address 2",
                  "label": "address 2",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "city",
                  "id": "city",
                  "label": "city",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": "city"
              },
              {
                  "columnName": "region",
                  "id": "region",
                  "label": "region",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "zone",
                  "id": "zone",
                  "label": "zone",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "district",
                  "id": "district",
                  "label": "district",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "territory",
                  "id": "territory",
                  "label": "territory",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "area",
                  "id": "area",
                  "label": "area",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "town",
                  "id": "town",
                  "label": "town",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "branch",
                  "id": "branch",
                  "label": "branch",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "pincode",
                  "id": "pincode",
                  "label": "pincode",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "state",
                  "id": "state",
                  "label": "state",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "country",
                  "id": "country",
                  "label": "country",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "latitude",
                  "id": "latitude",
                  "label": "latitude",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "longitude",
                  "id": "longitude",
                  "label": "longitude",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "tinno",
                  "id": "tinno",
                  "label": "tinno",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "gstno",
                  "id": "gstno",
                  "label": "gstno",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet FSSAI Number",
                  "id": "Outlet FSSAI Number",
                  "label": "Outlet FSSAI Number",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet PAN",
                  "id": "Outlet PAN",
                  "label": "Outlet PAN",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet Payment mode",
                  "id": "Outlet Payment mode",
                  "label": "Outlet Payment mode",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Sales Mode",
                  "id": "Sales Mode",
                  "label": "Sales Mode",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet Credit Limit",
                  "id": "Outlet Credit Limit",
                  "label": "Outlet Credit Limit",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet Outstanding Balance",
                  "id": "Outlet Outstanding Balance",
                  "label": "Outlet Outstanding Balance",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "TCS eligibility",
                  "id": "TCS eligibility",
                  "label": "TCS eligibility",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Key Account(Y/N)",
                  "id": "Key Account(Y/N)",
                  "label": "Key Account(Y/N)",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet Bank",
                  "id": "Outlet Bank",
                  "label": "Outlet Bank",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet IFSC Code",
                  "id": "Outlet IFSC Code",
                  "label": "Outlet IFSC Code",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet Account Number",
                  "id": "Outlet Account Number",
                  "label": "Outlet Account Number",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet Cash Discount",
                  "id": "Outlet Cash Discount",
                  "label": "Outlet Cash Discount",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "IsTaxable",
                  "id": "IsTaxable",
                  "label": "IsTaxable",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Credit days",
                  "id": "Credit days",
                  "label": "Credit days",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Outlet Discount (%)",
                  "id": "Outlet Discount (%)",
                  "label": "Outlet Discount (%)",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Discount group",
                  "id": "Discount group",
                  "label": "Discount group",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "priceListId",
                  "id": "priceListId",
                  "label": "priceListId",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "parent-0-1-id",
                  "id": "parent-0-1-id",
                  "label": "parent-0-1-id",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Loyalty balance",
                  "id": "Loyalty balance",
                  "label": "Loyalty balance",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Wallet balance",
                  "id": "Wallet balance",
                  "label": "Wallet balance",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "prodauthcode",
                  "id": "prodauthcode",
                  "label": "prodauthcode",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "BirthDt",
                  "id": "BirthDt",
                  "label": "BirthDt",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "AnnivDt",
                  "id": "AnnivDt",
                  "label": "AnnivDt",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "Supply Chain",
                  "id": "Supply Chain",
                  "label": "Supply Chain",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              },
              {
                  "columnName": "ContactPerson Name",
                  "id": "ContactPerson Name",
                  "label": "ContactPerson Name",
                  "mandatory": false,
                  "showInTable": false,
                  "tableField": ""
              }
          ]
      },
      "salesHierarchyFilter": {
          "enabled": true,
          "levelFilterLabel": "Select Sales Level",
          "levelFilterField": "salesLevel",
          "valueFilterLabel": "Select Sales Person",
          "valueFilterField": "salesValue",
          "hierarchyOrder": ["saleshead", "nsm", "asm", "rsm", "ase", "sde", "supplier"]
      },
      "geographicalHierarchyFilter": {
          "enabled": true,
          "levelFilterLabel": "Select Geographical Level",
          "levelFilterField": "geographicalLevel",
          "valueFilterLabel": "Select Geographical Value",
          "valueFilterField": "geographicalValue",
          "hierarchyOrder": ["country", "region", "state", "district", "city"]
      },
      "distributorFilter": {
          "enabled": true,
          "label": "Select Distributor",
          "field": "distributor"
      }
  },
  {
    "id": "report-2",
    "name": "Sales Details Report",
    "getAPI": "/table-generic/search?",
    "reportName": "sales_details_report",
    "isDistributorView": false,
    "description": "This report contains sales details data.",
    "templateUrl": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoeuat/images/OutletPOC4-ehOerjjxnywv.xlsx",
    "mandatory": true,
    "filterConfiguration": {
      "columns": []
    }
  },
  {
    "id": "report-3",
    "name": "GRN Listing Report",
    "getAPI": "/table-generic/search?",
    "reportName": "grn_listing_report",
    "isDistributorView": false,
    "description": "This report contains GRN listing data.",
    "templateUrl": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoeuat/images/OutletPOC4-ehOerjjxnywv.xlsx",
    "mandatory": true,
    "filterConfiguration": {
      "columns": []
    }
  },
  {
    "id": "report-4",
    "name": "Input Tax Report",
    "getAPI": "/table-generic/search?",
    "reportName": "input_tax_report",
    "isDistributorView": false,
    "description": "This report contains distributor data.",
    "templateUrl": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoeuat/images/OutletPOC4-ehOerjjxnywv.xlsx",
    "mandatory": true,
    "filterConfiguration": {
      "columns": []
    }
  },
  {
    "id": "report-5",
    "name": "Output Tax Report",
    "getAPI": "/table-generic/search?",
    "reportName": "output_tax_report",
    "isDistributorView": false,
    "description": "This report contains distributor data.",
    "templateUrl": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoeuat/images/OutletPOC4-ehOerjjxnywv.xlsx",
    "mandatory": true,
    "filterConfiguration": {
      "columns": []
    }
  },
  {
    "id": "report-6",
    "name": "Distributor Master Report",
    "getAPI": "/table-generic/search?",
    "reportName": "master_report_distributor",
    "isDistributorView": false,
    "description": "This report contains distributor data.",
    "templateUrl": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoeuat/images/OutletPOC4-ehOerjjxnywv.xlsx",
    "mandatory": true,
    "filterConfiguration": {
      "columns": []
    }
  }
];

